import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { erpApi } from '@/shared/axios/erp-api';
import { urlBackend } from '@/shared/constants';
import {
  AlertSeverityEnum,
  EntidadesFinancieras,
  EntidadesFinancierasPagingRes,
  UseMutationsParams,
} from '@/shared/interfaces';
import { useUiStore } from '@/store/ui';
import { getUrlParamsFetch, handleAxiosError } from '@/shared';

const { get, post, put } = erpApi();

///* tanSctack query
export const useFetchEntidadesFinancieras = ({
  page = 1,
  page_size = 10,
  nombre = '',
  enabled = true
}: GetEntidadesFinancierasParams) => {
  return useQuery({
    queryKey: ['entidades-financieras', page, nombre, page_size],
    queryFn: async () => {
      return getEntidadesFinancieras({ page, nombre, page_size });
    },
    enabled
  });
};

export const useGetEntidadFinanciera = (id: string) => {
  return useQuery({
    queryKey: ['entidades-financieras', id],
    queryFn: async () => {
      return getEntidadFinanciera(id);
    },
  });
};

export const useCreateEntidadesFinancieras = ({
  navigate,

  enableRedirect = true,
  returnUrl,

  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,

  enableSuccessAlert = true,
  customSuccessMessage,
}: UseMutationsParams) => {
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: createEntidadesFinancieras,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entidades-financieras'] });

      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Entidad financiera creada correctamente',
          AlertSeverityEnum.success
        );
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));

      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

export const useUpdateEntidadesFinancieras = ({
  navigate,
  enableRedirect = true,
  returnUrl,
  returnErrorUrl,
  enableErrorRedirect = false,
  customMessageErrorAlert,
  enableSuccessAlert = true,
  customSuccessMessage,
  onSuccess
}: UseMutationsParams & { onSuccess?: () => void }) => { // Update this line
  const queryClient = useQueryClient();
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  const setAlert = useUiStore.getState().setAlert;

  return useMutation({
    mutationFn: updateEntidadesFinancieras,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['type-regimen'] });
      enableRedirect && navigate && returnUrl && navigate(returnUrl);

      enableSuccessAlert &&
        setAlert &&
        setAlert(
          true,
          customSuccessMessage ?? 'Tipo regimen actualizado correctamente',
          AlertSeverityEnum.success
        );

      onSuccess && onSuccess(); // Add this line
    },
    onError: error => {
      enableErrorRedirect &&
        navigate &&
        returnUrl &&
        navigate(returnErrorUrl ?? (returnUrl || ''));
      handleAxiosError(error, customMessageErrorAlert);
    },
    onSettled: () => {
      setIsGlobalLoading(false);
    },
  });
};

///* axios
export type GetEntidadesFinancierasParams = {
  page?: number
  page_size?: number
  nombre?: string
  enabled?: boolean
};

export type CreateEntidadesFinancierasParams = Omit<
  EntidadesFinancieras,
  'id_entidad_financiera'
>;
export type UpdateEntidadesFinancierasParams = {
  id: string;
  data: CreateEntidadesFinancierasParams;
};

export const getEntidadesFinancieras = async (
  params?: GetEntidadesFinancierasParams
) => {
  const queryParams = getUrlParamsFetch({ ...params });
  return get<EntidadesFinancierasPagingRes>(
    `${urlBackend}api/v1/entidad-financiera/?${queryParams}`,
    true
  );
};

export const getEntidadFinanciera = async (id: string) => {
  try {
    return await get<EntidadesFinancieras>(`${urlBackend}api/v1/entidad-financiera/${id}/`, true);
  }
  catch (error) {
    handleAxiosError(error);
  }
};

export const createEntidadesFinancieras = async (
  data: CreateEntidadesFinancierasParams
) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return post<EntidadesFinancieras>(`${urlBackend}api/v1/entidad-financiera/`, data, true);

};

export const updateEntidadesFinancieras = async ({
  id,
  data,
}: UpdateEntidadesFinancierasParams) => {
  const setIsGlobalLoading = useUiStore.getState().setIsGlobalLoading;
  setIsGlobalLoading(true);
  return put<EntidadesFinancieras>(`${urlBackend}api/v1/entidad-financiera/${id}/`, data, true);
};
